import { httpsCallable } from '../firebase/app';

/**
 * #2109 — `uploadFile.js`'s upload callables used to hand back a permanent
 * public GCS URL (`https://storage.googleapis.com/<bucket>/<path>`), and
 * that URL got persisted on Firestore documents (`photoUrl`, `logoUrl`, and
 * every attachment record's `downloadURL`). Those callables now return a
 * short-lived SIGNED URL instead, so any value already stored on a document
 * is either a bare storage path (new records) or a legacy public URL (old
 * records) — never something safe to render directly forever.
 *
 * Extracts the bare object path (`calculationAttachments/1002/7/...`) out of
 * either shape, so callers can hand it to `getAttachmentUrl` below
 * regardless of which shape a given document happens to hold.
 *
 * @param {?string} value A stored `photoUrl`/`logoUrl`/`downloadURL`/`storagePath` value.
 * @return {?string} The bare object path, or `null` if there is nothing to resolve.
 */
export function extractStoragePath(value) {
  if (!value) {
    return null;
  }
  if (!/^https?:\/\//i.test(value)) {
    return value; // already a bare path
  }
  try {
    const url = new URL(value);
    const segments = url.pathname.replace(/^\/+/, '').split('/');
    segments.shift(); // bucket name
    const path = decodeURIComponent(segments.join('/'));
    return path || null;
  } catch {
    return null;
  }
}

/**
 * Asks the `getAttachmentUrl` Callable Function (#2109) for a fresh,
 * authorized, short-lived signed URL for a file this app previously
 * uploaded. Re-authorizes on every call — see
 * `functions/storage/getAttachmentUrl.js`.
 *
 * @param {string} storagePath The bare object path (see `extractStoragePath`).
 * @return {Promise<{url: string, expiresAt: number}>}
 */
export async function getAttachmentUrl(storagePath) {
  const fn = httpsCallable('getAttachmentUrl');
  const { data } = await fn({ storagePath });
  return data;
}
